import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { RouteReuseStrategy } from "@angular/router";
import { HttpClientModule } from "@angular/common/http";
import { environment } from "../environments/environment";
import { Capacitor } from '@capacitor/core';

import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { provideFirebaseApp, initializeApp, getApp } from "@angular/fire/app";
import {
  provideFirestore,
  initializeFirestore,
} from "@angular/fire/firestore";
import { ScreenTrackingService, UserTrackingService } from "@angular/fire/analytics";

import { getAuth, provideAuth } from "@angular/fire/auth";
import { indexedDBLocalPersistence, initializeAuth } from "firebase/auth";
import { getStorage, provideStorage } from "@angular/fire/storage";


import { IonicModule, IonicRouteStrategy } from "@ionic/angular";

import { AppComponent } from "./app.component";
import { CheersToastComponent } from "./components/cheers-toast/cheers-toast.component";
import { AppRoutingModule } from "./app-routing.module";
import { AuthService } from "./services/auth.service";

import { LetDirective, PushPipe } from "@ngrx/component";

// import * as Sentry from 'sentry-cordova';
import * as Sentry from "@sentry/browser";
// import { RewriteFrames } from '@sentry/integrations'
import { SentryIonicErrorHandler } from "./shared-modules/sentry-ionic-error-handler";
import { isIgnorableSentryEvent } from "./shared-modules/sentry-noise-filter";
import { ServiceWorkerModule } from "@angular/service-worker";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import packageJson from "../../package.json";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { StoreRouterConnectingModule } from "@ngrx/router-store"
import * as fromUser from "./state/user/reducer";
import * as fromUnits from "./state/units/reducer";
import * as fromBar from "./state/bar/reducer";
import * as fromBarSettings from "./state/bar-settings/reducer";
import * as fromKeg from "./state/keg/reducer";
import * as fromCo2Bottle from "./state/co2-bottle/reducer";
import { Co2BottleEffects } from "./state/co2-bottle/effects";
import * as fromFermenter from "./state/fermenter/reducer";
import { FermenterEffects } from "./state/fermenter/effects";
import * as fromTap from "./state/tap/reducer";
import * as fromTower from "./state/tower/reducer";
import * as fromPlaato2 from "./state/plaato2";
import * as fromPlaato from "./state/plaato/reducer";
import * as fromKegtron from "./state/kegtron/reducer";
import * as fromCustomKegMon from "./state/custom_keg_mon/reducer";
import * as fromOnTapTvActiveDisplay from "./state/on-tap-tv-active-displays";
import * as fromBeverage from "./state/beverage/reducer";
import * as fromBeerStyle from "./state/beer-style/reducer";
import * as fromExternalApiSettings from "./state/external-api-settings/reducer";
import * as fromServingSize from "./state/serving-size/reducer";
import * as fromStatistics from "./state/statistics/reducer";
import * as fromCheers from "./state/cheers/reducer";
import * as fromSubscription from "./state/subscription/reducer";
import * as fromVoucher from "./state/voucher";
import { UnitsEffects } from "./state/units/effects";
import { BarEffects } from "./state/bar/effects";
import { StatisticEffects } from "./state/statistics/effects";
import { BeverageEffects } from "./state/beverage/effects";
import { BeerStyleEffects } from "./state/beer-style/effects";
import { TapEffects } from "./state/tap/effects";
import { UserEffects } from "./state/user/effects";
import { KegEffects } from "./state/keg/effects";
import { ServingSizeEffects } from "./state/serving-size/effects";
import { LocationEffects } from "./state/location/effects";
import * as fromLoation from "./state/location/reducer";
import { NgxParticlesModule } from "@tsparticles/angular";
import { PlaatoEffects } from "./state/plaato/effects";
import { KegtronEffects } from "./state/kegtron/effects";
import { CustomKegMonEffects } from "./state/custom_keg_mon/effects";
import { ExternalApiSettingsEffects } from "./state/external-api-settings/effects";
import { SubscriptionEffects } from "./state/subscription/effects";
import { BarSettingsEffects } from "./state/bar-settings/effects";
import * as fromPlaatoStatus from "./state/plaato-status/";
import * as fromFont from "./state/font/";
import * as fromOnTapAdvanced from 'app/state/on_tap_advanced';
import * as fromOnTapBasic from 'app/state/on_tap_basic/reducer';
import { OnTapBasicEffects } from 'app/state/on_tap_basic/effects';
import * as fromOnTapTemplate from 'app/state/ontap-template';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
//import { NgrxRouterStoreModule } from "./state/router/ngrx-router.module";
import * as fromNotification from './state/notification';
import * as fromStripeCustomer from './state/stripeCustomer';
import { metaReducers, reducers } from "./state/app.state";
import * as fromAdvancedCardTemplate from "app/state/on_tap_advanced_card_template";

import * as fromStripeProduct from 'app/state/stripeProduct';
import * as fromStripePrice from 'app/state/stripePrice';
import * as fromStripeSubscription from 'app/state/stripeSubscription';
import { NgcCookieConsentModule } from "ngx-cookieconsent";
import { cookieConfig } from "./cookie-consent/cookie-consent";
import { persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { TowerEffects } from "./state/tower";
import * as fromBottle from 'app/state/bottle';
import * as fromCan from 'app/state/can';
import * as fromOnTapTvPendingConnections from 'app/state/on-tap-tv-pending-connections';
import * as fromRevenuecatCustomer from 'app/state/revenuecat-customer';
import * as fromRevenuecat from 'app/state/revenuecat';
import * as fromSupport from 'app/state/support';
import { SupportEffects } from 'app/state/support/effects';

Sentry.init({
  dsn: environment.sentry.dsn,
  environment: environment.production ? "prod" : "dev",
  release: environment.sentry.projectName + "@" + packageJson.version,
  // integrations: [
  //   new RewriteFrames()
  // ],
  // Drop known, non-actionable noise (transient/framework + permission-denied
  // from live listeners during logout). See sentry-noise-filter.ts.
  beforeSend: (event, hint) => (isIgnorableSentryEvent(event, hint) ? null : event),
});

@NgModule({
  imports: [
    CheersToastComponent,
    BrowserModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgcCookieConsentModule.forRoot(cookieConfig),
    ServiceWorkerModule.register("ngsw-worker.js", {
      enabled: environment.production && !Capacitor.isNativePlatform(),
    }),

    BrowserAnimationsModule,
    IonicModule.forRoot({
      mode: 'md'
    }),
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(),
    StoreRouterConnectingModule.forRoot(),
    AppRoutingModule,
    StoreDevtoolsModule.instrument({
      maxAge: 250,
      logOnly: environment.production,
      connectInZone: true
    }),
    StoreModule.forFeature(fromUser.stateName, fromUser.reducer),
    StoreModule.forFeature(fromBar.stateName, fromBar.reducer),
    StoreModule.forFeature(fromBarSettings.stateName, fromBarSettings.reducer),
    StoreModule.forFeature(fromUnits.stateName, fromUnits.reducer),
    StoreModule.forFeature(fromBeverage.stateName, fromBeverage.reducer),
    StoreModule.forFeature(fromBeerStyle.stateName, fromBeerStyle.reducer),
    StoreModule.forFeature(fromLoation.stateName, fromLoation.reducer),
    StoreModule.forFeature(fromCheers.stateName, fromCheers.reducer),
    StoreModule.forFeature(fromServingSize.stateName, fromServingSize.reducer),
    StoreModule.forFeature(fromStatistics.stateName, fromStatistics.reducer),
    StoreModule.forFeature(fromKeg.stateName, fromKeg.reducer),
    StoreModule.forFeature(fromCo2Bottle.stateName, fromCo2Bottle.reducer),
    StoreModule.forFeature(fromFermenter.stateName, fromFermenter.reducer),
    StoreModule.forFeature(fromBottle.stateName, fromBottle.reducer),
    StoreModule.forFeature(fromCan.stateName, fromCan.reducer),
    StoreModule.forFeature(fromTap.stateName, fromTap.reducer),
    StoreModule.forFeature(fromTower.stateName, fromTower.reducer),
    StoreModule.forFeature(fromPlaato2.stateName, fromPlaato2.reducer),
    StoreModule.forFeature(fromPlaato.stateName, fromPlaato.reducer),
    StoreModule.forFeature(fromKegtron.stateName, fromKegtron.reducer),
    StoreModule.forFeature(fromCustomKegMon.stateName, fromCustomKegMon.reducer),
    StoreModule.forFeature(fromExternalApiSettings.stateName, fromExternalApiSettings.reducer),
    StoreModule.forFeature(fromSubscription.stateName, fromSubscription.reducer),
    StoreModule.forFeature(fromPlaatoStatus.stateName, fromPlaatoStatus.reducer),
    StoreModule.forFeature(fromFont.stateName, fromFont.reducer),
    StoreModule.forFeature(fromOnTapAdvanced.stateName, fromOnTapAdvanced.reducer),
    StoreModule.forFeature(fromOnTapBasic.stateName, fromOnTapBasic.onTapBasicReducer),
    StoreModule.forFeature(fromOnTapTvActiveDisplay.stateName, fromOnTapTvActiveDisplay.reducer),

    StoreModule.forFeature(fromOnTapTvPendingConnections.stateName, fromOnTapTvPendingConnections.reducer),

    StoreModule.forFeature(fromOnTapTemplate.stateName, fromOnTapTemplate.reducer),
    StoreModule.forFeature(fromAdvancedCardTemplate.stateName, fromAdvancedCardTemplate.reducer),
    StoreModule.forFeature(fromNotification.stateName, fromNotification.reducer),
    StoreModule.forFeature(fromStripeCustomer.stateName, fromStripeCustomer.reducer),
    StoreModule.forFeature(fromStripeProduct.stateName, fromStripeProduct.reducer),
    StoreModule.forFeature(fromStripePrice.stateName, fromStripePrice.reducer),
    StoreModule.forFeature(fromStripeSubscription.stateName, fromStripeSubscription.reducer),
    StoreModule.forFeature(fromRevenuecatCustomer.stateName, fromRevenuecatCustomer.reducer),
    StoreModule.forFeature(fromRevenuecat.stateName, fromRevenuecat.reducer),
    StoreModule.forFeature(fromVoucher.stateName, fromVoucher.reducer),
    StoreModule.forFeature(fromSupport.stateName, fromSupport.reducer),

    EffectsModule.forFeature([
      UserEffects,
      BarEffects,
      BarSettingsEffects,
      UnitsEffects,
      BeverageEffects,
      BeerStyleEffects,
      LocationEffects,
      ServingSizeEffects,
      StatisticEffects,
      KegEffects,
      Co2BottleEffects,
      FermenterEffects,
      fromBottle.Effects,
      fromCan.Effects,
      TapEffects,
      TowerEffects,
      PlaatoEffects,
      fromPlaatoStatus.PlaatoStatusEffects,
      KegtronEffects,
      fromPlaato2.Effects,
      CustomKegMonEffects,
      ExternalApiSettingsEffects,
      SubscriptionEffects,
      fromFont.Effects,
      fromOnTapAdvanced.Effects,
      OnTapBasicEffects,
      OnTapBasicEffects,
      OnTapBasicEffects,
      fromOnTapTemplate.Effects,
      fromOnTapTvActiveDisplay.Effects,
      fromOnTapTvPendingConnections.Effects,
      fromAdvancedCardTemplate.Effects,
      fromNotification.Effects,
      fromStripeCustomer.Effects,
      fromStripeProduct.Effects,
      // fromStripePrice.Effects,
      fromStripeSubscription.Effects,
      fromRevenuecatCustomer.Effects,
      fromRevenuecat.Effects,
      fromVoucher.Effects,
      SupportEffects
    ]),
    PushPipe,
    LetDirective,
    NgxParticlesModule,





    // NgrxRouterStoreModule,

    //   provideAuth(() => getAuth()),
    //   provideAppCheck(() => {
    //     // TODO get a reCAPTCHA Enterprise here https://console.cloud.google.com/security/recaptcha?project=_
    //     const provider = new ReCaptchaEnterpriseProvider(environment.reCaptchaKey);
    //     return initializeAppCheck(undefined, { provider, isTokenAutoRefreshEnabled: true });
    //   }),
    //   //provideFirestore(() => getFirestore()),
    //   provideFunctions(() => getFunctions()),
    //   //provideMessaging(() => getMessaging())
    // )
  ],
  providers: [
    AuthService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: SentryIonicErrorHandler },
    ScreenTrackingService,
    UserTrackingService,
    provideFirebaseApp(() => {
      const app = initializeApp(environment.firebase);
      if (Capacitor.isNativePlatform() === true) {
        initializeAuth(app, {
          persistence: indexedDBLocalPersistence
        });
      }
      return app;
    }),
    provideRemoteConfig(() => getRemoteConfig()),
    provideMessaging(() => getMessaging()),
    provideAuth(() => {
      if (Capacitor.isNativePlatform() === true) {
        return initializeAuth(getApp(), {
          persistence: indexedDBLocalPersistence
        });
      }
      else {
        const auth = getAuth();
        return auth;
      }
    }),
    provideFirestore(() => {
      // Multi-tab persistence: all tabs share one IndexedDB cache so that
      // OnTap TV screens / a user with the bar open in multiple tabs ALL get
      // offline persistence (single-tab only gives the first tab a cache).
      // NB: keep experimentalForceLongPolling: false — the old multi-tab
      // variant (deprecated enableMultiTabIndexedDbPersistence +
      // forceLongPolling: true) caused trouble; this modern API + auto
      // long-polling is a different matter. On native (WKWebView/Android WebView)
      // there is only one webview = effectively single-tab.
      // NOT deployed to mobile without device verification — see
      // MANUAL-TESTS.md (the Firestore multi-tab persistence section).
      const firestore = initializeFirestore(getApp(), {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
        experimentalForceLongPolling: false,
      });
      return firestore;
    }),
    provideStorage(() => getStorage()),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
  declarations: [AppComponent],
})
export class AppModule { }

